import React, { FC, useEffect, useState } from 'react'
import { observer } from 'mobx-react'
import { RouteComponentProps } from 'react-router-dom'
import { IonContent, IonPage, useIonViewWillEnter } from '@ionic/react'
import { MktEmpty, UseHeader } from '@cps'
import { ActivityAPI } from '@api/index'
import { storeMobx } from '@mobxStore'
import { getPlatform, useHistoryNew } from '@utils'
import mktController from 'controllers/mktController'
import defaultImg from '@img/default-longMallProduct.png'
import {
  formatDateOnlyRange,
  formatDateRange,
  getActivityDetailHtml,
  getActivityName,
  getActivitySummary,
  getCover,
  getEndAt,
  getSessionName,
  getStartAt,
  getStoreName,
  normalizeActivityDetail
} from '../utils'
import './index.scss'

interface IProps extends IPage, RouteComponentProps<{ activityId: string }> { }

type TExpandedSection = 'sessions' | 'stores' | ''

const { isWeixinMiniProgram } = getPlatform()

const getStoreId = (item: any) => item?.storeId || item?.id
const getStoreAddress = (item: any) => {
  const address = item?.address || item?.storeAddress || ''
  const addressDetails = item?.addressDetails || item?.addressDetail || ''
  if (addressDetails && address.includes(addressDetails)) return address
  return `${address}${addressDetails}`
}

const ActivityReserveDetail: FC<IProps> = ({ match }: IProps) => {
  const history = useHistoryNew()
  const { activityId } = match.params
  const [info, setInfo] = useState<any>({})
  const [previewUrl, setPreviewUrl] = useState<string>('')
  const [expandedSection, setExpandedSection] = useState<TExpandedSection>('')
  const [scrollTop, setScrollTop] = useState(0)

  const getData = () => {
    ActivityAPI.getActivityReserveDetail(activityId).then((res: any) => {
      if (!res?.errors) {
        setInfo(normalizeActivityDetail(res, storeMobx.viewStoreList || storeMobx.storeList || [storeMobx.info]))
      }
    }).catch(() => setInfo({}))
  }

  useIonViewWillEnter(() => {
    getData()
  })

  const activityDetailHtml = getActivityDetailHtml(info)
  const activitySummary = getActivitySummary(info)
  const hasActivityDetail = Boolean(
    activityDetailHtml.replace(/<[^>]*>/g, '').replace(/&nbsp;/g, '').trim() ||
    /<(img|video|iframe)\b/i.test(activityDetailHtml)
  )
  const coverUrl = getCover(info) || defaultImg
  const hasInfo = Boolean(info && Object.keys(info).length)
  const activityName = getActivityName(info)
  const headerTitle = hasInfo && activityName !== '--' ? activityName : i18n.t('mkt.activityReserve.detailTitle')
  const stores = info?.stores || info?.storeList || []
  const sessions = info?.sessions || info?.sessionList || []
  const storeMap = stores.reduce((map: Record<string, any>, store: any) => {
    const storeId = getStoreId(store)
    if (storeId) map[String(storeId)] = store
    return map
  }, {})

  const getSessionStoreNames = (session: any) => {
    const storeIds = session?.storeIds || session?.stores?.map((store: any) => getStoreId(store)) || []
    return storeIds
      .map((storeId: any) => storeMap[String(storeId)])
      .filter(Boolean)
      .map((store: any) => getStoreName(store))
      .join('、')
  }

  const toggleSection = (section: Exclude<TExpandedSection, ''>) => {
    setExpandedSection(current => current === section ? '' : section)
  }

  const previewImage = (url?: string) => {
    if (url) setPreviewUrl(url)
  }

  const handleRichTextClick = (event: React.MouseEvent<HTMLDivElement>) => {
    const target = event.target as HTMLElement
    const img = target.closest('img') as HTMLImageElement | null
    if (img?.src) previewImage(img.src)
  }

  const goBooking = () => {
    history.push(`/activityReserve/booking/${activityId}`)
  }

  useEffect(() => {
    if (!hasInfo) return

    mktController.initShareData({
      url: isWeixinMiniProgram ? `/pages/launch/index?jumpH5Page=${encodeURIComponent(location.href)}` : location.href,
      title: headerTitle,
      img: coverUrl,
    })
  }, [coverUrl, hasInfo, headerTitle])

  return (
    <IonPage className='activity-reserve-detail-page'>
      {scrollTop > 100
        ? <UseHeader title={headerTitle} onBack={() => mktController.goBack(history)} animation></UseHeader>
        : <div className='activity-detail-back' role='button' aria-label={i18n.t('common.back')} onClick={() => mktController.goBack(history)}><i className='iconfont icon-BACK-12x'></i></div>}
      <IonContent className='page-content' scrollEvents onIonScroll={(event: any) => setScrollTop(event.detail.scrollTop)}>
        {!hasInfo ? <MktEmpty /> : (
          <div className='activity-detail-body'>
            <div className='activity-hero' onClick={() => previewImage(coverUrl)}>
              <img src={coverUrl} alt={activityName} />
            </div>
            <section className='activity-main'>
              <h2>{activityName}</h2>
              <div className='activity-meta'>{formatDateOnlyRange(getStartAt(info), getEndAt(info))}</div>
              {activitySummary && <div className='activity-desc'>{activitySummary}</div>}
            </section>
            {hasActivityDetail && (
              <section className='activity-detail-section'>
                <div className='rich-text' onClick={handleRichTextClick} dangerouslySetInnerHTML={{ __html: activityDetailHtml }}></div>
              </section>
            )}
            {(sessions.length > 0 || stores.length > 0) && (
              <div className='activity-extra-info'>
                {sessions.length > 0 && (
                  <section className={`activity-info-group${expandedSection === 'sessions' ? ' expanded' : ''}`}>
                    <button className='activity-info-toggle' aria-expanded={expandedSection === 'sessions'} onClick={() => toggleSection('sessions')}>
                      <span>{i18n.t('mkt.activityReserve.activityTime')}</span>
                      <i className='iconfont icon-chevron-down'></i>
                    </button>
                    {expandedSection === 'sessions' && (
                      <div className='activity-info-list session-info-list'>
                        {sessions.map((session: any) => {
                          const storeNames = getSessionStoreNames(session)
                          return (
                            <div className='activity-info-item' key={session?.sessionId || session?.id}>
                              <strong>{getSessionName(session)}</strong>
                              <span>{formatDateRange(getStartAt(session), getEndAt(session))}</span>
                              {storeNames && <small>{storeNames}</small>}
                            </div>
                          )
                        })}
                      </div>
                    )}
                  </section>
                )}
                {stores.length > 0 && (
                  <section className={`activity-info-group${expandedSection === 'stores' ? ' expanded' : ''}`}>
                    <button className='activity-info-toggle' aria-expanded={expandedSection === 'stores'} onClick={() => toggleSection('stores')}>
                      <span>{i18n.t('mkt.activityReserve.activityAddress')}</span>
                      <i className='iconfont icon-chevron-down'></i>
                    </button>
                    {expandedSection === 'stores' && (
                      <div className='activity-info-list store-info-list'>
                        {stores.map((store: any) => {
                          const address = getStoreAddress(store)
                          return (
                            <div className='activity-info-item' key={getStoreId(store)}>
                              <strong>{getStoreName(store)}</strong>
                              {address && <span>{address}</span>}
                            </div>
                          )
                        })}
                      </div>
                    )}
                  </section>
                )}
              </div>
            )}
          </div>
        )}
      </IonContent>
      {previewUrl && (
        <div className='image-preview-mask' onClick={() => setPreviewUrl('')}>
          <img src={previewUrl} alt='' onClick={(event: React.MouseEvent<HTMLImageElement>) => event.stopPropagation()} />
        </div>
      )}
      {hasInfo && (
        <button className='activity-detail-float-button' onClick={goBooking}>{i18n.t('mkt.activityReserve.reserveNow')}</button>
      )}
    </IonPage>
  )
}

export default observer(ActivityReserveDetail)
